/** One row of the associations matrix — an own row (editable in edit mode) or an inherited
 *  one (read-only, muted, with Override) — plus its addon row (`renderRowAddon`).
 *
 *  States carry an icon or shape AND words (DESIGN.md Icon-And-Words): a changed cell is
 *  tinted amber with a corner mark and says "was …" in its tooltip and description; a
 *  removed row is struck through on the tint with a "removed" chip and Restore; a new row
 *  has a "new" chip; an inherited row says where it comes from; a lock gives its reason.
 *
 *  Memoized: the table keeps an unchanged row's `model` object and passes a stable
 *  `onAction`, so an edit re-renders only the rows it changes (and focus the two rows it
 *  moves between). A host's `renderCell` / `renderRowAddon` / `categoryActions` should be
 *  stable too — a new one re-renders every row. */
import AddIcon from '@mui/icons-material/Add';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ReplayIcon from '@mui/icons-material/Replay';
import RestoreIcon from '@mui/icons-material/Restore';
import { Box, Checkbox, Chip, IconButton, TableCell, TableRow, Tooltip } from '@mui/material';
import { memo, type ReactElement, type ReactNode } from 'react';

import { BoolText, visuallyHidden } from '@/components/BoolText';
import type { TriggerProps } from '@/components/DataGrid/CellActionsPopover';
import { ValueActions, type ValueAction } from '@/components/ValueActions/ValueActions';

import { describeRowChange } from './associationsReducer';
import { FLAG_BY_KEY, formatCounts, type Flag } from './flags';
import { AMBER_LAYER, CELL_FOCUS, CHANGED_MARK, CHANGED_TINT, MONO, ROW_PX } from './layout';
import type { ActionCol, RowModel } from './rowModel';
import { CountsText, TagsCountButton } from './TagsCountCell';
import { MAX_CHECKIN } from './rules';
import type { AssocAction, AssocCategory, AssocRow } from './types';

export interface AssociationRowProps {
  model: RowModel;
  flags: readonly Flag[];
  /** Flags that open a group (a hairline to their left). */
  groupStarts: ReadonlySet<Flag>;
  editable: boolean;
  /** The column of the grid's one tab stop, when it is in this row. */
  activeCol: string | null;
  /** All columns (for the addon row's span). */
  colCount: number;
  ownerText?: string;
  categoryActions?: (c: AssocCategory) => ValueAction[];
  renderCell?: (row: AssocRow, field: Flag | 'counts', cell: ReactNode) => ReactNode;
  renderRowAddon?: (row: AssocRow) => ReactNode;
  onAction: (a: AssocAction) => void;
}

export const AssociationRow = memo(function AssociationRow({
  model,
  flags,
  groupStarts,
  editable,
  activeCol,
  colCount,
  ownerText,
  categoryActions,
  renderCell,
  renderRowAddon,
  onAction,
}: AssociationRowProps) {
  const { row, name, live, kind } = model;
  const own = kind === 'own';
  const removed = row.state === 'removed';
  const overridden = Boolean(model.inherited?.overriddenBy);
  const nav = (col: string): TriggerProps => ({
    tabIndex: activeCol === col ? 0 : -1,
    'data-nav': `${model.navId}|${col}`,
  });
  const wrap = (field: Flag | 'counts', cell: ReactNode) =>
    own && renderCell ? renderCell(row, field, cell) : cell;
  // Opaque backgrounds: the sticky cells cover the flags scrolled under them.
  const rowBg = own ? 'background.paper' : 'background.default';
  const struck = removed ? { textDecoration: 'line-through' } : {};
  const addon = renderRowAddon?.(row);

  return (
    <>
      <TableRow
        data-row={model.navId}
        {...(own
          ? { 'data-tagcat-row': row.key }
          : { 'data-tagcat-default': String(row.id ?? row.key) })}
        data-removed={removed || undefined}
        data-overridden={overridden || undefined}
        data-first-inherited={model.firstInherited || undefined}
        sx={{ height: ROW_PX }}
      >
        <CategoryCell
          model={model}
          nav={nav('category')}
          bg={rowBg}
          ownerText={ownerText}
          actions={categoryActions && row.category ? categoryActions(row.category) : []}
          editable={editable}
        />
        <CountsCell
          model={model}
          nav={nav('counts')}
          rowBg={own ? undefined : rowBg}
          struck={struck}
          wrap={wrap}
          onAction={onAction}
        />
        {flags.map((f) => (
          <FlagCell
            key={f}
            model={model}
            flag={f}
            nav={nav(f)}
            groupStart={groupStarts.has(f)}
            rowBg={own ? undefined : rowBg}
            wrap={wrap}
            onAction={onAction}
          />
        ))}
        <TableCell
          data-col="actions"
          sx={{
            position: 'sticky',
            right: 0,
            zIndex: 1,
            bgcolor: rowBg,
            ...(removed ? AMBER_LAYER : {}),
            borderLeft: 1,
            borderLeftColor: 'divider',
            p: 0,
            px: 0.5,
          }}
        >
          <RowActions
            model={model}
            name={name}
            nav={nav}
            onAction={onAction}
            live={live}
            editable={editable}
          />
        </TableCell>
      </TableRow>
      {addon != null && addon !== false && (
        <TableRow data-tagcat-addon={row.key}>
          <TableCell colSpan={colCount} sx={{ py: 0, px: 1, bgcolor: rowBg }}>
            {/* Sticky, so it stays in view while the flags scroll sideways. */}
            <Box
              sx={{
                position: 'sticky',
                left: 8,
                width: 'fit-content',
                maxWidth: 'calc(100vw - 64px)',
              }}
            >
              {addon}
            </Box>
          </TableCell>
        </TableRow>
      )}
    </>
  );
});

function CategoryCell({
  model,
  nav,
  bg,
  ownerText,
  actions,
  editable,
}: {
  model: RowModel;
  nav: TriggerProps;
  bg: string;
  ownerText?: string;
  actions: ValueAction[];
  editable: boolean;
}) {
  const { row, name, kind } = model;
  const removed = row.state === 'removed';
  const muted = kind === 'inherited';
  const code = row.category?.name && row.category.code ? row.category.code : null;
  const label = (
    <Box
      component="span"
      sx={{ fontWeight: 600, ...(removed ? { textDecoration: 'line-through' } : {}) }}
    >
      {name}
    </Box>
  );
  const hasActions = actions.length > 0;
  const pending = editable && (row.state === 'new' || removed);
  return (
    <TableCell
      component="th"
      scope="row"
      data-col="category"
      {...(hasActions ? {} : nav)}
      sx={{
        position: 'sticky',
        left: 0,
        zIndex: 1,
        bgcolor: bg,
        ...(pending ? AMBER_LAYER : {}),
        borderRight: 1,
        borderRightColor: 'divider',
        py: 0,
        px: 1,
        fontWeight: 400,
        ...CELL_FOCUS,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
        <Box
          component="span"
          sx={{
            flex: '1 1 auto',
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            fontSize: 14,
            color: muted ? 'text.secondary' : 'text.primary',
          }}
        >
          {hasActions ? (
            <ValueActions
              actions={actions}
              title={code ? `${name} · ${code}` : name}
              triggerProps={nav}
            >
              {label}
            </ValueActions>
          ) : (
            label
          )}
          {code && (
            <Box
              component="span"
              sx={{ ml: 0.75, fontFamily: MONO, fontSize: 12, color: 'text.secondary' }}
            >
              {code}
            </Box>
          )}
        </Box>
        <RowChip model={model} ownerText={ownerText} editable={editable} />
      </Box>
    </TableCell>
  );
}

const CHIP_SX = {
  height: 18,
  fontSize: 11,
  flex: 'none',
  maxWidth: 104,
  '& .MuiChip-label': { px: 0.75 },
  '& .MuiChip-icon': { fontSize: 13, ml: 0.5, mr: -0.25 },
} as const;
const PENDING_CHIP_SX = {
  ...CHIP_SX,
  borderColor: 'warning.main',
  color: 'warning.dark',
  '& .MuiChip-icon': { ...CHIP_SX['& .MuiChip-icon'], color: 'warning.dark' },
} as const;

/** One chip saying what the row is: new, removed, inherited (from whom), overridden. */
function RowChip({
  model,
  ownerText,
  editable,
}: {
  model: RowModel;
  ownerText?: string;
  editable: boolean;
}) {
  const { row, name } = model;
  if (model.kind === 'inherited') {
    const overridden = Boolean(model.inherited?.overriddenBy);
    const text = overridden ? 'overridden' : (ownerText ?? 'inherited');
    const tip = overridden
      ? `${name} has its own row, which applies instead`
      : `${ownerText ?? 'Inherited'} — read-only here${editable ? '; Override copies it into the own categories' : ''}`;
    return (
      <Tooltip title={tip} describeChild>
        <Chip
          size="small"
          variant="outlined"
          label={text}
          data-chip={overridden ? 'overridden' : 'inherited'}
          sx={{ ...CHIP_SX, color: 'text.secondary' }}
        />
      </Tooltip>
    );
  }
  if (!editable) return null;
  if (row.state === 'new') {
    return (
      <Tooltip title={`${name} is added when you save`} describeChild>
        <Chip
          size="small"
          variant="outlined"
          icon={<AddIcon />}
          label="new"
          data-chip="new"
          sx={PENDING_CHIP_SX}
        />
      </Tooltip>
    );
  }
  if (row.state === 'removed') {
    return (
      <Tooltip title={`${name} is deleted when you save — Restore keeps it`} describeChild>
        <Chip
          size="small"
          variant="outlined"
          icon={<DeleteOutlineIcon />}
          label="removed"
          data-chip="removed"
          sx={PENDING_CHIP_SX}
        />
      </Tooltip>
    );
  }
  return null;
}

/** The changed tint, the corner mark, and the words ("was …") for a cell. */
function changedWords(was: string) {
  return `Changed — was ${was}`;
}

function CountsCell({
  model,
  nav,
  rowBg,
  struck,
  wrap,
  onAction,
}: {
  model: RowModel;
  nav: TriggerProps;
  rowBg?: string;
  struck: object;
  wrap: (field: Flag | 'counts', cell: ReactNode) => ReactNode;
  onAction: (a: AssocAction) => void;
}) {
  const { row, live, base, name } = model;
  const changed = model.changed.has('counts');
  const was = changed && base ? changedWords(formatCounts(base.min_tags, base.max_tags)) : null;
  const content = live ? (
    <TagsCountButton
      min={row.min_tags}
      max={row.max_tags}
      name={name}
      navProps={nav}
      onSet={(min_tags, max_tags) =>
        onAction({ type: 'setCounts', key: row.key, min_tags, max_tags })
      }
    />
  ) : (
    <Box component="span" sx={struck}>
      <CountsText
        min={row.min_tags}
        max={row.max_tags}
        muted={model.kind === 'inherited' || row.state === 'removed'}
      />
    </Box>
  );
  const cell = (
    <TableCell
      data-col="counts"
      data-state={changed ? 'changed' : undefined}
      {...(live ? {} : nav)}
      sx={[
        { p: 0, px: 0.5, textAlign: 'center', ...CELL_FOCUS },
        rowBg ? { bgcolor: rowBg } : {},
        row.state === 'removed' ? { bgcolor: CHANGED_TINT } : {},
        changed ? { bgcolor: CHANGED_TINT, ...CHANGED_MARK } : {},
        TINT_FADE,
      ]}
    >
      {wrap('counts', content)}
      {was && (
        <Box component="span" sx={visuallyHidden}>
          {was}
        </Box>
      )}
    </TableCell>
  );
  return withTip(was, cell);
}

const TINT_FADE = {
  transition: 'background-color 150ms ease-out',
  '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
} as const;

/** Always the same element tree, with or without words: wrapping the cell only once it
 *  changes would remount it — and drop the focus of the checkbox just toggled. */
function withTip(title: string | null, cell: ReactElement) {
  return (
    <Tooltip title={title ?? ''} describeChild placement="top">
      {cell}
    </Tooltip>
  );
}

function FlagCell({
  model,
  flag,
  nav,
  groupStart,
  rowBg,
  wrap,
  onAction,
}: {
  model: RowModel;
  flag: Flag;
  nav: TriggerProps;
  groupStart: boolean;
  rowBg?: string;
  wrap: (field: Flag | 'counts', cell: ReactNode) => ReactNode;
  onAction: (a: AssocAction) => void;
}) {
  const { row, live, base, name } = model;
  const on = row.flags[flag];
  const lock = live ? (model.locks[flag] ?? null) : null;
  const changed = model.changed.has(flag);
  const removed = row.state === 'removed';
  // The cell itself is the focus stop when there is no live control in it.
  const cellIsStop = !live || Boolean(lock);
  const order = flag === 'checkin' && on ? model.order : null;
  const label = `${FLAG_BY_KEY[flag].short} of ${name}`;

  let content: ReactNode;
  if (live) {
    content = (
      <Box component="span" sx={{ position: 'relative', display: 'inline-flex' }}>
        <Checkbox
          size="small"
          checked={on}
          disabled={Boolean(lock)}
          onChange={(e) => onAction({ type: 'setFlag', key: row.key, flag, to: e.target.checked })}
          inputProps={{ 'aria-label': label, ...(cellIsStop ? {} : nav) }}
          sx={{ p: '3px' }}
        />
        {order !== null && <OrderSup order={order} />}
      </Box>
    );
  } else if (order !== null) {
    content = <OrderMark order={order} muted={model.kind === 'inherited'} />;
  } else {
    content = (
      <BoolText on={on} onText="" offText="" muted={model.kind === 'inherited' || removed} />
    );
  }

  const words = [lock, changed && base ? changedWords(base.flags[flag] ? 'on' : 'off') : null]
    .filter(Boolean)
    .join(' · ');
  const cell = (
    <TableCell
      data-col={flag}
      data-cell={flag}
      data-state={changed ? 'changed' : undefined}
      data-locked={lock ? 'true' : undefined}
      {...(cellIsStop ? nav : {})}
      sx={[
        { p: 0, textAlign: 'center', ...CELL_FOCUS },
        groupStart ? { borderLeft: 1, borderLeftColor: 'divider' } : {},
        rowBg ? { bgcolor: rowBg } : {},
        removed ? { bgcolor: CHANGED_TINT } : {},
        changed ? { bgcolor: CHANGED_TINT, ...CHANGED_MARK } : {},
        TINT_FADE,
      ]}
    >
      {wrap(flag, content)}
      {words && (
        <Box component="span" sx={visuallyHidden}>
          {words}
        </Box>
      )}
    </TableCell>
  );
  return withTip(words || null, cell);
}

/** The check-in order in view mode: a numbered disc (amber past the limit). */
function OrderMark({ order, muted }: { order: number; muted?: boolean }) {
  const over = order > MAX_CHECKIN;
  return (
    <Box
      component="span"
      data-checkin-order={order}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 18,
        height: 18,
        borderRadius: '50%',
        fontSize: 11,
        fontWeight: 700,
        fontVariantNumeric: 'tabular-nums',
        color: '#fff',
        bgcolor: over ? 'warning.dark' : muted ? 'text.secondary' : 'primary.main',
      }}
    >
      <span aria-hidden>{order}</span>
      <Box component="span" sx={visuallyHidden}>
        {`on, check-in order ${order}${over ? ', over the limit' : ''}`}
      </Box>
    </Box>
  );
}

/** The check-in order beside a checked box in edit mode. */
function OrderSup({ order }: { order: number }) {
  const over = order > MAX_CHECKIN;
  return (
    <Box
      component="span"
      data-checkin-order={order}
      sx={{
        position: 'absolute',
        top: -1,
        right: -5,
        fontSize: 10,
        fontWeight: 700,
        lineHeight: 1,
        fontVariantNumeric: 'tabular-nums',
        color: over ? 'warning.dark' : 'primary.main',
        pointerEvents: 'none',
      }}
    >
      <span aria-hidden>{order}</span>
      <Box component="span" sx={visuallyHidden}>
        {`check-in order ${order}`}
      </Box>
    </Box>
  );
}

const ACTION_SX = { p: 0.25, width: 26, height: 26 } as const;

/** Four fixed slots — up · down · revert · remove/restore/override — kept in view mode too
 *  (the cell keeps its width; nothing moves on Edit). */
function RowActions({
  model,
  name,
  nav,
  live,
  editable,
  onAction,
}: {
  model: RowModel;
  name: string;
  nav: (col: string) => TriggerProps;
  live: boolean;
  editable: boolean;
  onAction: (a: AssocAction) => void;
}) {
  const { row } = model;
  const btn = (
    col: ActionCol,
    tip: string,
    aria: string,
    icon: ReactNode,
    onClick: () => void,
    disabled = false,
  ) => {
    const button = (
      <IconButton
        size="small"
        aria-label={aria}
        disabled={disabled}
        onClick={onClick}
        sx={ACTION_SX}
        {...(disabled ? { tabIndex: -1 } : nav(col))}
      >
        {icon}
      </IconButton>
    );
    // A disabled button says nothing (an empty title never opens) — but keeps its Tooltip,
    // so enabling it does not remount the button under the keyboard.
    return (
      <Tooltip title={disabled ? '' : tip} describeChild>
        {button}
      </Tooltip>
    );
  };
  let slots: [ReactNode, ReactNode, ReactNode, ReactNode] = [null, null, null, null];
  if (live) {
    slots = [
      btn(
        'act:up',
        'Move up',
        `Move ${name} up`,
        <ArrowUpwardIcon sx={{ fontSize: 16 }} />,
        () => onAction({ type: 'move', key: row.key, delta: -1 }),
        !model.canUp,
      ),
      btn(
        'act:down',
        'Move down',
        `Move ${name} down`,
        <ArrowDownwardIcon sx={{ fontSize: 16 }} />,
        () => onAction({ type: 'move', key: row.key, delta: 1 }),
        !model.canDown,
      ),
      model.base && model.changes.length > 0
        ? btn(
            'act:revert',
            `Revert: ${describeRowChange(model.changes)}`,
            `Revert ${name}`,
            <ReplayIcon sx={{ fontSize: 16 }} />,
            () => onAction({ type: 'revertRow', key: row.key, to: model.base! }),
          )
        : null,
      btn(
        'act:remove',
        row.state === 'new' ? 'Remove this new category' : 'Remove — deleted on save',
        `Remove ${name}`,
        <DeleteOutlineIcon sx={{ fontSize: 18 }} />,
        () => onAction({ type: 'remove', key: row.key }),
      ),
    ];
  } else if (editable && row.state === 'removed') {
    slots[3] = btn(
      'act:restore',
      'Restore — keep this category',
      `Restore ${name}`,
      <RestoreIcon sx={{ fontSize: 18 }} />,
      () => onAction({ type: 'restore', key: row.key }),
    );
  } else if (model.actions.includes('act:override') && model.inherited) {
    const from = model.inherited;
    slots[3] = btn(
      'act:override',
      'Override — copy every setting into the own categories, to change it here',
      `Override ${name}`,
      <ContentCopyIcon sx={{ fontSize: 16 }} />,
      () => onAction({ type: 'override', from }),
    );
  }
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 26px)',
        justifyContent: 'end',
        alignItems: 'center',
      }}
    >
      {slots.map((node, i) => (
        <Box key={i} sx={{ width: 26, height: 26, display: 'flex' }}>
          {node}
        </Box>
      ))}
    </Box>
  );
}
